import debounce from 'lodash.debounce';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ImprovedSelect from './ImprovedSelect';
import InsuranceLogo from '../../Insurance/InsuranceLogo';
import { useFormContext } from 'react-hook-form';
import { API_BASE_URL } from '../../../api';

const InputInsurance = ({
  name,
  handleChange,
  errors,
  validationRules = {},
  allowCustomValue = false,
  className,
  placeholder = 'Sélectionner une assurance...'
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const { getValues } = useFormContext();

  const fetchInsurances = useCallback(async (keyword = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/healthinsurances`, {
        withCredentials: true,
        params: keyword ? { keyword } : undefined,
      });
      const formatted = (data?.content || []).map((ins) => ({
        value: ins.id,
        label: ins.name,
        icon: <InsuranceLogo logoUrl={ins.logoUrl} name={ins.name} size={24} />,
      }));
      setOptions(formatted);
    } catch (error) {
      console.error('Failed to fetch health insurances', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsurances();
  }, [fetchInsurances]);

  const debouncedSearch = useMemo(() => debounce(fetchInsurances, 300), [fetchInsurances]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    const initializeDefaultValue = async () => {
      const id = getValues(name);
      if (!id || options.find((o) => o.value === id)) return;
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/v1/healthinsurances/${id}`, {
          withCredentials: true,
        });
        if (data && inputRef.current) {
          const externalValue = {
            value: data.id,
            label: data.name,
            icon: <InsuranceLogo logoUrl={data.logoUrl} name={data.name} size={24} />,
          };
          inputRef.current.setExternalValue(externalValue);
          setOptions((prev) =>
            prev.some((p) => p.value === externalValue.value) ? prev : [...prev, externalValue]
          );
        }
      } catch (error) {
        console.error('Erreur fetch insurance by id:', error);
      }
    };
    initializeDefaultValue();
  }, [getValues, name]);

  return (
    <ImprovedSelect
      ref={inputRef}
      name={name}
      multiple={false}
      options={options}
      onChange={handleChange}
      onInputChange={debouncedSearch}
      validationRules={validationRules}
      allowCustomValue={allowCustomValue}
      loading={loading}
      errors={errors}
      className={className}
      placeholder={placeholder}
    />
  );
};

export default InputInsurance;
